import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface AuthUser {
  id: string;
  nombre: string;
  correo: string;
  /** Id de rol/perfil (slug): built-in o personalizado. */
  rol: string;
  rolLabel: string;
  /** Permisos efectivos del rol (para gating de UI por permiso). */
  permisos: string[];
  especialidad?: string;
  registroProfesional?: string;
  firmaUrl?: string;
  /** Si es `true`, debe cambiar su contraseña temporal antes de operar. */
  mustChangePassword?: boolean;
  /** `true` = súper administrador (cuenta protegida del dueño). */
  esSuperAdmin?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  /**
   * Access token JWT. Vive SOLO en memoria — no se persiste en localStorage
   * (XSS) ni sessionStorage. Si la página se recarga, queda `null` y el
   * cliente API hace refresh implícito usando la cookie httpOnly del backend.
   */
  accessToken: string | null;
  isHydrated: boolean;
  login(user: AuthUser, accessToken: string): void;
  logout(): void;
  setAccessToken(token: string): void;
  /** Marca que el usuario ya cambió su contraseña temporal. */
  clearMustChangePassword(): void;
  markHydrated(): void;
}

const STORAGE_KEY = 'datazo.auth';

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isHydrated: false,
      login: (user, accessToken) => set({ user, accessToken }),
      logout: () => set({ user: null, accessToken: null }),
      setAccessToken: (token) => set({ accessToken: token }),
      clearMustChangePassword: () =>
        set((s) => (s.user ? { user: { ...s.user, mustChangePassword: false } } : {})),
      markHydrated: () => set({ isHydrated: true }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage),
      // Solo persistimos el `user` (metadata UI). El `accessToken` vive en RAM.
      partialize: (state) => ({ user: state.user }),
      onRehydrateStorage: () => {
        return (rehydrated) => {
          rehydrated?.markHydrated();
        };
      },
    },
  ),
);
